'use client';

import type { ConPriceMoleculeProps } from '@gandalf/babylon/molecules/price';
import { Product, Translations } from '@frodo/models/types';
import { currencyManager } from '@frodo/lib/utils/common/currencyManager';
import { COUNTRY_IT } from '@frodo/lib/utils/common/constants';
import { useCustomerInfo } from '@frodo/lib/hooks/domain/useCustomerInfo';
import { useStore } from '@frodo/lib/hooks/useStore';

const getPriceBottomText = (item: Product): string | undefined => {
  if (item.packingQuantity) {
    return `${item.packingQuantity.value} ${item.packingQuantity.label}`;
  }
};

export function useProductInfo(item: Product, trans: Translations) {
  const store = useStore();
  const { formatPrice } = currencyManager(store);
  const { isB2B } = useCustomerInfo();

  const isBundle = item.variety?.toLowerCase() === 'bundle';
  const isConfigProduct = item.variety === 'config';
  const salesPriceValue = isB2B
    ? item.merchantSuggestedRetailPrice
    : item.grossMerchantSuggestedRetailPrice;
  const mainPrice = isB2B ? item.finalNetPrice : item.finalGrossPrice;
  const basePrice = isB2B ? item.baseNetPrice : item.baseGrossPrice;

  const pricePostfix =
    (isB2B
      ? trans['product.hint.tax_exclusive']
      : trans['product.hint.tax_inclusive']) +
    (item.basePriceUnit && basePrice
      ? ` (${formatPrice(basePrice)} / ${item.basePriceUnit})`
      : '');

  const salesPrice =
    item.hasSalesPrice && !isBundle ? formatPrice(salesPriceValue) : '';

  const salesLabel = item.hasSalesPrice
    ? `-${item.salesPercentage} %`
    : undefined;

  const salesText = item.hasSalesPrice
    ? isBundle
      ? trans['bundle.price.strikethrough.explanation']
      : trans['product.srp']
    : undefined;

  const { lastPurchasedDate, lastOrderNumber } = item.lastPurchaseDate ?? {};
  const bottomText = getPriceBottomText(item);

  const price: Omit<
    ConPriceMoleculeProps,
    'priceSize' | 'isLoading' | 'alignRight'
  > = {
    priceText: formatPrice(mainPrice, { showPrefix: isConfigProduct }),
    pricePostfix,
    bottomText: bottomText ?? '',
    hasSalesBlock: true,
    salesBlockMode: store.country === COUNTRY_IT ? 'multi-line' : 'single-line',
    salesLabel,
    salesPrice,
    salesText,
    isSalesTextBold: !isBundle,
  };

  return {
    isBundle,
    isConfigProduct,
    price,
    lastPurchasedDate,
    lastOrderNumber,
  };
}
